import React, { useEffect, useState } from "react";
import { Link, NavLink } from "react-router-dom";
import { Phone, MessageCircle, Menu, X } from "lucide-react";
import { Button } from "@/components/ui/button";
import { BRAND, CONTACT, waLink } from "@/config/brand";
import { HEADER } from "@/constants/testIds";

const nav = [
    { to: "/", label: "Home" },
    { to: "/services", label: "Services" },
    { to: "/areas", label: "Areas" },
    { to: "/moving-tips", label: "Moving Tips" },
    { to: "/about", label: "About" },
    { to: "/contact", label: "Contact" },
];

export const Header = () => {
    const [open, setOpen] = useState(false);
    const [scrolled, setScrolled] = useState(false);

    useEffect(() => {
        const onScroll = () => setScrolled(window.scrollY > 8);
        onScroll();
        window.addEventListener("scroll", onScroll, { passive: true });
        return () => window.removeEventListener("scroll", onScroll);
    }, []);

    return (
        <header
            data-testid={HEADER.root}
            className={`sticky top-0 z-40 w-full bg-[#001B3D] text-white transition-[box-shadow,background-color] duration-200 ${
                scrolled ? "shadow-[0_4px_24px_rgba(0,27,61,0.25)]" : ""
            }`}
        >
            <div className="container-ys flex items-center gap-3 py-3">
                <Link
                    to="/"
                    data-testid={HEADER.logo}
                    className="flex items-center gap-3"
                    onClick={() => setOpen(false)}
                    aria-label={`${BRAND.name} — home`}
                >
                    <img
                        src={BRAND.logo}
                        alt={BRAND.logoAlt}
                        className="h-11 w-11 rounded-md object-contain shrink-0"
                        width={44}
                        height={44}
                    />
                    <span className="font-heading font-bold text-lg tracking-tight leading-tight">
                        <span className="block">YS Removals</span>
                        <span className="block text-[10px] font-medium tracking-[0.18em] uppercase text-white/70">Your Man With Van</span>
                    </span>
                </Link>

                <nav
                    data-testid={HEADER.nav}
                    className="ml-6 hidden lg:flex items-center gap-1 text-sm"
                >
                    {nav.map((item) => (
                        <NavLink
                            key={item.to}
                            to={item.to}
                            data-testid={`nav-link-${item.to.replace(/\//g, "") || "home"}`}
                            className={({ isActive }) =>
                                `rounded-md px-3 py-2 transition-colors duration-200 hover:bg-white/10 ${
                                    isActive ? "text-white" : "text-white/85"
                                }`
                            }
                        >
                            {item.label}
                        </NavLink>
                    ))}
                </nav>

                <div className="ml-auto flex items-center gap-2">
                    <a
                        data-testid={HEADER.call}
                        href={CONTACT.phoneHref}
                        className="hidden md:inline-flex items-center gap-2 rounded-md px-3 py-2 text-sm text-white/90 hover:text-white hover:bg-white/10"
                    >
                        <Phone className="h-4 w-4" strokeWidth={1.75} />
                        {CONTACT.phoneDisplay}
                    </a>
                    <a
                        data-testid={HEADER.whatsapp}
                        href={waLink()}
                        target="_blank"
                        rel="noreferrer"
                        className="hidden sm:inline-flex items-center gap-2 rounded-md bg-[#25D366] hover:bg-[#20b955] px-3 py-2 text-sm font-medium text-white shadow-sm transition-colors duration-200"
                    >
                        <MessageCircle className="h-4 w-4" strokeWidth={1.75} />
                        WhatsApp
                    </a>
                    <Link to="/request-a-quote" className="hidden sm:block">
                        <Button
                            data-testid={HEADER.quote}
                            className="bg-white hover:bg-white/90 text-[#001B3D] font-semibold"
                        >
                            Get a Free Quote
                        </Button>
                    </Link>
                    <button
                        type="button"
                        data-testid={HEADER.mobileMenuToggle}
                        aria-label="Menu"
                        onClick={() => setOpen((v) => !v)}
                        className="inline-flex lg:hidden h-10 w-10 items-center justify-center rounded-md hover:bg-white/10"
                    >
                        {open ? <X className="h-5 w-5" /> : <Menu className="h-5 w-5" />}
                    </button>
                </div>
            </div>

            {open && (
                <div className="lg:hidden border-t border-white/10">
                    <nav className="container-ys py-3 flex flex-col gap-1 text-sm">
                        {nav.map((item) => (
                            <NavLink
                                key={item.to}
                                to={item.to}
                                onClick={() => setOpen(false)}
                                data-testid={`nav-mobile-${item.to.replace(/\//g, "") || "home"}`}
                                className={({ isActive }) =>
                                    `rounded-md px-3 py-2 hover:bg-white/10 ${
                                        isActive ? "bg-white/10" : ""
                                    }`
                                }
                            >
                                {item.label}
                            </NavLink>
                        ))}
                        <div className="flex gap-2 pt-2">
                            <a
                                href={CONTACT.phoneHref}
                                className="flex-1 inline-flex items-center justify-center gap-2 rounded-md border border-white/25 px-3 py-2 text-sm"
                            >
                                <Phone className="h-4 w-4" /> Call
                            </a>
                            <a
                                href={waLink()}
                                target="_blank"
                                rel="noreferrer"
                                className="flex-1 inline-flex items-center justify-center gap-2 rounded-md bg-[#25D366] hover:bg-[#20b955] px-3 py-2 text-sm font-medium text-white"
                            >
                                <MessageCircle className="h-4 w-4" /> WhatsApp
                            </a>
                            <Link
                                to="/request-a-quote"
                                onClick={() => setOpen(false)}
                                className="flex-1 inline-flex items-center justify-center rounded-md bg-white text-[#001B3D] px-3 py-2 text-sm font-semibold"
                            >
                                Get a Quote
                            </Link>
                        </div>
                    </nav>
                </div>
            )}
        </header>
    );
};
